import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog'
import type { ColumnDefinition, DataTableItem } from './types'

interface DataTableColumnSettingsProps<T extends DataTableItem> {
  open: boolean
  onOpenChange: (open: boolean) => void
  columns: ColumnDefinition<T>[]
  visibleColumns: string[]
  onSave: (columns: string[]) => void
}

export function DataTableColumnSettings<T extends DataTableItem>({
  open,
  onOpenChange,
  columns,
  visibleColumns,
  onSave
}: DataTableColumnSettingsProps<T>) {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(visibleColumns)

  useEffect(() => {
    if (open) {
      setSelectedColumns(visibleColumns)
    }
  }, [open, visibleColumns])

  const toggleColumn = (columnId: string) => {
    const column = columns.find(c => c.id === columnId)
    if (column?.required) return

    setSelectedColumns(prev => {
      if (prev.includes(columnId)) {
        return prev.filter(id => id !== columnId)
      } else {
        return [...prev, columnId]
      }
    })
  }

  const handleSave = () => {
    onSave(selectedColumns)
    onOpenChange(false)
  }

  const optionalColumns = columns.filter(col => !col.required)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Configure Columns</DialogTitle>
          <DialogDescription>Select which columns to display in the table.</DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="space-y-1">
            {optionalColumns.map((column) => (
              <div key={column.id} className="flex items-center space-x-3">
                <Checkbox
                  id={`col-${column.id}`}
                  checked={selectedColumns.includes(column.id)}
                  onCheckedChange={() => toggleColumn(column.id)}
                />
                <label
                  htmlFor={`col-${column.id}`}
                  className="text-sm cursor-pointer"
                >
                  {column.label}
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Set columns
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
